import type {
  AssetKind,
  AssetRegistry,
  AssetRegistryEntry,
  MysteryRoomCase,
  MysteryRoomChallengeParams,
  MysteryRoomClue,
  MysteryRoomFinalResult,
  MysteryRoomResolvedScore,
  MysteryRoomRunState,
  MysteryRoomScoreInput,
  NormalizedSceneModel,
  ResolvedAsset,
  RoomState,
  SceneModel,
  VisualAssetRef,
  WorldEvent,
} from './contracts';

const MOSCOW_TIMEZONE = 'Europe/Moscow';
const MYSTERY_ROOM_PATH = '/games/mystery-room';
const FALLBACK_ASSET_ORDER: AssetKind[] = ['vector', 'image', 'layered-image', 'hybrid'];
const MAX_SCORE_TURNS = 12;

type SearchParamReader =
  | URLSearchParams
  | {
      get(name: string): string | null;
    }
  | Record<string, string | string[] | undefined>;

export function resolveMysteryRoomDailySeed(date = new Date(), timeZone = MOSCOW_TIMEZONE): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return formatter.format(date);
}

export function sanitizeMysteryRoomSeed(seed?: string | null, fallbackDate = new Date(), timeZone = MOSCOW_TIMEZONE): string {
  if (typeof seed === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(seed)) {
    return seed;
  }
  return resolveMysteryRoomDailySeed(fallbackDate, timeZone);
}

export function calculateMysteryRoomScore(input: MysteryRoomScoreInput): MysteryRoomResolvedScore {
  const cluesFound = clampWhole(input.cluesFound, 0, 12);
  const criticalCluesFound = clampWhole(input.criticalCluesFound, 0, cluesFound);
  const turnsRemaining = clampWhole(input.turnsRemaining, 0, MAX_SCORE_TURNS);
  const coverageBonus = cluesFound * 60;
  const criticalBonus = criticalCluesFound * 90;
  const verdictBonus = input.verdictCorrect ? 420 : 0;
  const objectiveBonus = input.objectiveSecured ? 320 : 0;
  const turnBonus = turnsRemaining * 20;

  return {
    cluesFound,
    criticalCluesFound,
    verdictCorrect: Boolean(input.verdictCorrect),
    objectiveSecured: Boolean(input.objectiveSecured),
    turnsRemaining,
    coverageBonus,
    criticalBonus,
    verdictBonus,
    objectiveBonus,
    turnBonus,
    score: coverageBonus + criticalBonus + verdictBonus + objectiveBonus + turnBonus,
  };
}

export function buildMysteryRoomChallengePath(params: MysteryRoomChallengeParams): string {
  const seed = sanitizeMysteryRoomSeed(params.seed);
  const search = new URLSearchParams({ seed });

  if (typeof params.target === 'number' && Number.isFinite(params.target) && params.target > 0) {
    search.set('target', String(Math.floor(params.target)));
  }
  if (typeof params.from === 'string' && params.from.trim()) {
    search.set('from', params.from.trim().slice(0, 40));
  }

  return `${MYSTERY_ROOM_PATH}?${search.toString()}`;
}

export function buildMysteryRoomChallengeUrl(origin: string, params: MysteryRoomChallengeParams): string {
  const normalizedOrigin = origin.replace(/\/+$/, '');
  return `${normalizedOrigin}${buildMysteryRoomChallengePath(params)}`;
}

export function parseMysteryRoomChallengeParams(
  params: SearchParamReader,
  fallbackDate = new Date(),
  timeZone = MOSCOW_TIMEZONE,
): MysteryRoomChallengeParams {
  const seed = sanitizeMysteryRoomSeed(readParam(params, 'seed'), fallbackDate, timeZone);
  const targetRaw = readParam(params, 'target');
  const fromRaw = readParam(params, 'from');
  const parsedTarget = Number.parseInt(String(targetRaw ?? ''), 10);

  return {
    seed,
    target: Number.isFinite(parsedTarget) && parsedTarget > 0 ? parsedTarget : undefined,
    from: typeof fromRaw === 'string' && fromRaw.trim() ? fromRaw.trim().slice(0, 40) : undefined,
  };
}

export function createAssetRegistry(entries: AssetRegistryEntry[]): AssetRegistry {
  const map = new Map<string, ResolvedAsset>();
  for (const entry of entries) {
    map.set(makeRegistryKey(entry), entry.resolved);
  }
  return { entries: map };
}

export function resolveMysteryRoomAsset(registry: AssetRegistry, asset: VisualAssetRef): ResolvedAsset {
  const kinds = [asset.kind, ...FALLBACK_ASSET_ORDER.filter((kind) => kind !== asset.kind)];
  const keys = buildLookupKeys(asset);

  for (const lookup of keys) {
    for (const kind of kinds) {
      const resolved = registry.entries.get(makeRegistryKey({ ...lookup, kind }));
      if (resolved) {
        return resolved;
      }
    }
  }

  return {
    type: 'component',
    className: 'bg-slate-900/70',
    alt: asset.id,
  };
}

export function normalizeMysteryRoomScene(scene: SceneModel): NormalizedSceneModel {
  return {
    ...scene,
    characters: [...(scene.characters || [])],
    props: [...(scene.props || [])].sort((left, right) => {
      const zDelta = (left.zIndex ?? 0) - (right.zIndex ?? 0);
      if (zDelta !== 0) return zDelta;
      if (left.y !== right.y) return left.y - right.y;
      return left.x - right.x;
    }),
    effects: [...(scene.effects || [])],
    lighting: scene.lighting ?? { id: 'mystery' },
    dialogue: [...(scene.dialogue || [])],
  };
}

export function createInitialMysteryRoomRunState(caseData: MysteryRoomCase): MysteryRoomRunState {
  const roomStates: Record<string, RoomState> = {};

  for (const room of caseData.rooms) {
    roomStates[room.id] = {
      roomId: room.id,
      visits: room.id === caseData.entryRoomId ? 1 : 0,
      status: 'stable',
      visibleClueIds: [...room.initialClueIds],
      foundClueIds: [],
    };
  }

  return {
    activeRoomId: caseData.entryRoomId,
    turnsSpent: 0,
    turnsRemaining: caseData.turnLimit,
    foundClueIds: [],
    criticalClueIds: [],
    visitedRoomIds: [caseData.entryRoomId],
    roomStates,
    objective: {
      status: 'stable',
      deadlineTurn: 6,
      note: 'Образец нестабилен. Нужно запечатать криокамеру до того, как цепочка хранения сорвётся.',
    },
    eventIdsTriggered: [],
    log: [caseData.sceneIntro],
  };
}

export function buildMysteryRoomActiveScene(caseData: MysteryRoomCase, state: MysteryRoomRunState): SceneModel {
  const room = getRoom(caseData, state.activeRoomId);
  const roomState = state.roomStates[state.activeRoomId];
  const visibleClues = new Set(roomState?.visibleClueIds ?? []);
  const props = room.scene.props.filter((prop) => !prop.interactive || visibleClues.has(prop.id));
  const dialogue = room.scene.dialogue ? [...room.scene.dialogue] : [];

  if (roomState?.lastEventNote) {
    dialogue.push({
      id: `room-note-${room.id}-${state.eventIdsTriggered.length}`,
      speakerName: room.title,
      text: roomState.lastEventNote,
    });
  }

  return normalizeMysteryRoomScene({
    ...room.scene,
    location: {
      ...room.scene.location,
      state: roomState?.status ?? room.scene.location.state,
      visual: {
        ...room.scene.location.visual,
        state: roomState?.status ?? room.scene.location.visual.state,
      },
    },
    props,
    dialogue,
  });
}

export function moveMysteryRoomRun(caseData: MysteryRoomCase, state: MysteryRoomRunState, toRoomId: string): MysteryRoomRunState {
  const currentRoom = getRoom(caseData, state.activeRoomId);
  if (!currentRoom.connections.some((connection) => connection.toRoomId === toRoomId)) {
    return state;
  }

  const next = advanceTurns(caseData, state, caseData.actionCost.move);
  next.activeRoomId = toRoomId;
  next.visitedRoomIds = pushUnique(next.visitedRoomIds, toRoomId);
  next.roomStates[toRoomId] = {
    ...next.roomStates[toRoomId],
    visits: (next.roomStates[toRoomId]?.visits ?? 0) + 1,
  };

  next.log = [
    ...next.log,
    {
      id: `move-${state.activeRoomId}-${toRoomId}-${next.turnsSpent}`,
      speakerName: 'Навигация',
      text: `Переход: ${currentRoom.title} → ${getRoom(caseData, toRoomId).title}.`,
    },
  ];

  return applyTriggeredEvents(caseData, next);
}

export function inspectMysteryRoomClue(caseData: MysteryRoomCase, state: MysteryRoomRunState, clueId: string): MysteryRoomRunState {
  const roomState = state.roomStates[state.activeRoomId];
  if (!roomState?.visibleClueIds.includes(clueId) || state.foundClueIds.includes(clueId)) {
    return state;
  }

  const clue = getClue(caseData, clueId);
  const next = advanceTurns(caseData, state, caseData.actionCost.inspect);

  next.foundClueIds = [...next.foundClueIds, clue.id];
  next.criticalClueIds =
    clue.importance === 'critical' ? pushUnique(next.criticalClueIds, clue.id) : [...next.criticalClueIds];
  next.roomStates[state.activeRoomId] = {
    ...next.roomStates[state.activeRoomId],
    foundClueIds: [...next.roomStates[state.activeRoomId].foundClueIds, clue.id],
  };
  next.log = [...next.log, clue.dialogue];

  return applyTriggeredEvents(caseData, next);
}

export function stabilizeMysteryRoomObjective(caseData: MysteryRoomCase, state: MysteryRoomRunState): MysteryRoomRunState {
  const room = getRoom(caseData, state.activeRoomId);
  const action = room.objectiveAction;
  if (!action || state.objective.status === 'secured' || state.objective.status === 'lost') {
    return state;
  }

  const next = advanceTurns(caseData, state, action.cost);
  next.objective = {
    ...next.objective,
    status: 'secured',
    note: 'Криокамера запечатана. Образец остаётся в безопасном контуре до финального отчёта.',
    securedTurn: next.turnsSpent,
  };
  next.roomStates[state.activeRoomId] = {
    ...next.roomStates[state.activeRoomId],
    status: 'sealed',
    lastEventNote: action.detail,
  };
  next.log = [
    ...next.log,
    {
      id: `${action.id}-${next.turnsSpent}`,
      speakerName: 'Операция',
      text: action.detail,
    },
  ];

  return applyTriggeredEvents(caseData, next);
}

export function finishMysteryRoomRun(
  caseData: MysteryRoomCase,
  state: MysteryRoomRunState,
  accusedSuspectId?: string | null,
): MysteryRoomFinalResult {
  const culprit = caseData.suspects.find((suspect) => suspect.id === caseData.culpritId);
  const accused = caseData.suspects.find((suspect) => suspect.id === accusedSuspectId);
  const verdictCorrect = accusedSuspectId === caseData.culpritId;
  const objectiveSecured = state.objective.status === 'secured';
  const score = calculateMysteryRoomScore({
    cluesFound: state.foundClueIds.length,
    criticalCluesFound: state.criticalClueIds.length,
    verdictCorrect,
    objectiveSecured,
    turnsRemaining: state.turnsRemaining,
  });

  return {
    ...score,
    culpritId: caseData.culpritId,
    culpritName: culprit?.name ?? 'Неизвестно',
    accusedSuspectId: accusedSuspectId ?? null,
    accusedSuspectName: accused?.name ?? null,
    objectiveStatus: state.objective.status,
    operationSummary: objectiveSecured
      ? 'Образец стабилизирован и цепочка хранения не сорвалась.'
      : state.objective.status === 'lost'
        ? 'Операционный провал: образец ушёл из безопасного контура до финального отчёта.'
        : 'Цепочка хранения осталась под риском: объект не был стабилизирован вовремя.',
    verdictSummary: verdictCorrect
      ? `Вердикт подтверждён: ${culprit?.name ?? 'нужный подозреваемый'} организовал(а) подмену.`
      : `Ошибка в обвинении. Настоящий организатор — ${culprit?.name ?? 'неизвестный сотрудник'}.`,
    foundClueIds: [...state.foundClueIds],
  };
}

function advanceTurns(caseData: MysteryRoomCase, state: MysteryRoomRunState, cost: number): MysteryRoomRunState {
  const nextTurnsSpent = clampWhole(state.turnsSpent + Math.max(0, cost), 0, caseData.turnLimit);

  return {
    ...state,
    turnsSpent: nextTurnsSpent,
    turnsRemaining: Math.max(0, caseData.turnLimit - nextTurnsSpent),
    foundClueIds: [...state.foundClueIds],
    criticalClueIds: [...state.criticalClueIds],
    visitedRoomIds: [...state.visitedRoomIds],
    roomStates: cloneRoomStates(state.roomStates),
    objective: { ...state.objective },
    eventIdsTriggered: [...state.eventIdsTriggered],
    log: [...state.log],
  };
}

function applyTriggeredEvents(caseData: MysteryRoomCase, state: MysteryRoomRunState): MysteryRoomRunState {
  let next = state;

  for (const event of caseData.events) {
    if (next.eventIdsTriggered.includes(event.id) || next.turnsSpent < event.triggerTurn) {
      continue;
    }

    next = applyEvent(next, event);
  }

  return next;
}

function applyEvent(state: MysteryRoomRunState, event: WorldEvent): MysteryRoomRunState {
  const next = {
    ...state,
    roomStates: cloneRoomStates(state.roomStates),
    objective: { ...state.objective },
    eventIdsTriggered: [...state.eventIdsTriggered, event.id],
    log: [...state.log],
  };

  if (event.roomId) {
    next.roomStates[event.roomId] = {
      ...next.roomStates[event.roomId],
    };
  }

  if (event.type === 'hide_clue' && event.roomId && event.clueId) {
    next.roomStates[event.roomId].visibleClueIds = next.roomStates[event.roomId].visibleClueIds.filter((id) => id !== event.clueId);
    if (event.revealClueId) {
      next.roomStates[event.roomId].visibleClueIds = pushUnique(next.roomStates[event.roomId].visibleClueIds, event.revealClueId);
    }
  }

  if (event.type === 'reveal_clue' && event.roomId && event.revealClueId) {
    next.roomStates[event.roomId].visibleClueIds = pushUnique(next.roomStates[event.roomId].visibleClueIds, event.revealClueId);
  }

  if (event.type === 'room_status' && event.roomId && event.status) {
    next.roomStates[event.roomId].status = event.status;
  }

  if (event.type === 'objective_risk' && next.objective.status !== 'secured' && next.objective.status !== 'lost') {
    next.objective.status = 'at-risk';
    next.objective.note = event.dialogue?.text ?? next.objective.note;
  }

  if (event.type === 'objective_loss' && next.objective.status !== 'secured') {
    next.objective.status = 'lost';
    next.objective.note = event.dialogue?.text ?? next.objective.note;
  }

  if (event.dialogue) {
    next.log.push(event.dialogue);
    if (event.roomId) {
      next.roomStates[event.roomId].lastEventNote = event.dialogue.text;
    }
  }

  return next;
}

function cloneRoomStates(roomStates: Record<string, RoomState>): Record<string, RoomState> {
  return Object.fromEntries(
    Object.entries(roomStates).map(([roomId, roomState]) => [
      roomId,
      {
        ...roomState,
        visibleClueIds: [...roomState.visibleClueIds],
        foundClueIds: [...roomState.foundClueIds],
      },
    ]),
  );
}

function clampWhole(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.floor(value)));
}

function readParam(params: SearchParamReader, key: string): string | undefined {
  if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
    return params.get(key) ?? undefined;
  }
  if (typeof (params as { get?: unknown }).get === 'function') {
    return ((params as { get(name: string): string | null }).get(key) ?? undefined) || undefined;
  }

  const value = (params as Record<string, string | string[] | undefined>)[key];
  if (Array.isArray(value)) return value[0];
  return value;
}

function buildLookupKeys(asset: VisualAssetRef): Array<Omit<AssetRegistryEntry, 'resolved' | 'kind'>> {
  const variants: Array<Omit<AssetRegistryEntry, 'resolved' | 'kind'>> = [];
  const states = [asset.state, undefined];
  const themes = [asset.theme, undefined];
  const variantsList = [asset.variant, undefined];

  for (const variant of variantsList) {
    for (const theme of themes) {
      for (const state of states) {
        const key = { id: asset.id, variant, theme, state };
        if (!variants.some((existing) => makeRegistryKey({ ...existing, kind: 'vector' }) === makeRegistryKey({ ...key, kind: 'vector' }))) {
          variants.push(key);
        }
      }
    }
  }

  return variants;
}

function makeRegistryKey(entry: Pick<AssetRegistryEntry, 'id' | 'kind' | 'variant' | 'theme' | 'state'>): string {
  return [entry.id, entry.kind, entry.variant ?? '', entry.theme ?? '', entry.state ?? ''].join('|');
}

function pushUnique(values: string[], nextValue: string): string[] {
  return values.includes(nextValue) ? [...values] : [...values, nextValue];
}

function getRoom(caseData: MysteryRoomCase, roomId: string) {
  const room = caseData.rooms.find((candidate) => candidate.id === roomId);
  if (!room) {
    throw new Error(`Unknown Mystery Room room: ${roomId}`);
  }
  return room;
}

function getClue(caseData: MysteryRoomCase, clueId: string): MysteryRoomClue {
  const clue = caseData.clues.find((candidate) => candidate.id === clueId);
  if (!clue) {
    throw new Error(`Unknown Mystery Room clue: ${clueId}`);
  }
  return clue;
}
